import React, { useContext, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
    Box,
    Button,
    CssBaseline,
    Divider,
    FormControl,
    Stack,
    TextField,
    Card as MuiCard,
    Typography,
    CircularProgress
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AuthContext from "../contexts/AuthContext";
import {toast} from "react-toastify";
//import Lottie from "lottie-react";
//import anime6 from "../animations/anime6.json";

const Card = styled(MuiCard)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignSelf: "center",
    width: "100%",
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    margin: "auto",
    borderRadius: '20px',
    background: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    [theme.breakpoints.up("sm")]: {
        width: "450px"
    },
    ...theme.applyStyles('dark', {
        background: 'rgba(30, 30, 30, 0.9)',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
    }),
}));

const SignUpContainer = styled(Stack)(({ theme }) => ({
    minHeight: "100vh",
    padding: theme.spacing(2),
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    position: 'relative',
    [theme.breakpoints.up("sm")]: {
        padding: theme.spacing(4)
    },
    "&::before": {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(45deg, rgba(52, 152, 219, 0.1) 0%, rgba(155, 89, 182, 0.1) 100%)',
        zIndex: 0,
    },
}));

const StyledButton = styled(Button)(({ theme }) => ({
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '25px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    textTransform: 'none',
    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
        background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
    },
    '&:disabled': {
        background: 'linear-gradient(135deg, #a0a0a0 0%, #808080 100%)',
        transform: 'none',
        boxShadow: 'none',
    },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        background: 'rgba(255, 255, 255, 0.8)',
        transition: 'all 0.3s ease',
        '&:hover': {
            background: 'rgba(255, 255, 255, 0.9)',
            transform: 'translateY(-1px)',
        },
        '&.Mui-focused': {
            background: 'rgba(255, 255, 255, 1)',
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.2)',
        },
    },
}));

const PageTitle = styled(Typography)(({ theme }) => ({
    fontSize: '2.2rem',
    fontWeight: '700',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    textAlign: 'center',
    marginBottom: '8px',
}));

const SubTitle = styled(Typography)(({ theme }) => ({
    color: '#7f8c8d',
    textAlign: 'center',
    fontSize: '14px',
    lineHeight: '1.5',
    marginBottom: '16px',
}));

const StyledLink = styled(Link)(({ theme }) => ({
    color: '#667eea',
    textDecoration: 'none',
    fontWeight: '500',
    transition: 'all 0.3s ease',
    '&:hover': {
        color: '#764ba2',
        textDecoration: 'underline',
    },
}));

const SigninPage = () => {
    const [emailError, setEmailError] = useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = useState("");
    const [passwordError, setPasswordError] = useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = useState("");
    const [loading, setLoading] = useState(false);

    const { login } = useContext(AuthContext);
    const navigate = useNavigate();

    const validateInputs = () => {
        const email = document.getElementById("email");
        const password = document.getElementById("password");
        let isValid = true;

        if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
            setEmailError(true);
            setEmailErrorMessage("Lütfen geçerli bir e-posta adresi girin.");
            isValid = false;
        } else {
            setEmailError(false);
            setEmailErrorMessage("");
        }

        if (!password.value || password.value.length < 6) {
            setPasswordError(true);
            setPasswordErrorMessage("Şifre en az 6 karakter olmalıdır.");
            isValid = false;
        } else {
            setPasswordError(false);
            setPasswordErrorMessage("");
        }

        return isValid;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!validateInputs()) return;

        const data = new FormData(event.currentTarget);
        const payload = {
            email: data.get("email"),
            password: data.get("password")
        };

        try {
            setLoading(true);

            const response = await fetch(`${process.env.REACT_APP_API_URL}/signIn-jwt`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                let errorMessage = "Giriş başarısız.";
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.message || errorMessage;
                } catch {
                    const text = await response.text();
                    if (text) errorMessage = text;
                }
                throw new Error(errorMessage);
            }

            const data = await response.json();
            console.log("Giriş başarılı:", data);
            login(data.email, data.token, data.firstName, data.lastName, data.id);
            navigate("/");

        } catch (error) {
            toast.error("Hata: " + error.message);
            setPasswordError(true);
            setPasswordErrorMessage(error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="SigninPage">
            <CssBaseline enableColorScheme />
            <SignUpContainer direction="column" justifyContent="space-between">
                <Card variant="outlined">
                    <PageTitle>Giriş Yap</PageTitle>
                    <SubTitle>
                        Hoşgeldin. Lütfen bilgilerini gir.
                    </SubTitle>
                    <Divider />

                    <Box
                        component="form"
                        onSubmit={handleSubmit}
                        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                        <FormControl>
                            <StyledTextField
                                required
                                fullWidth
                                id="email"
                                name="email"
                                label="E-mail"
                                placeholder="your@email.com"
                                autoComplete="email"
                                variant="outlined"
                                error={emailError}
                                helperText={emailErrorMessage}
                            />
                        </FormControl>
                        <FormControl>
                            <StyledTextField
                                required
                                fullWidth
                                id="password"
                                name="password"
                                type="password"
                                label="Şifre"
                                placeholder="••••••"
                                autoComplete="current-password"
                                variant="outlined"
                                error={passwordError}
                                helperText={passwordErrorMessage}
                            />
                        </FormControl>

                        <StyledButton
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={24} /> : "Giriş Yap"}
                        </StyledButton>
                    </Box>

                    <Divider />

                    <p align="center" style={{ color: "gray" }}>
                        Henüz hesabın yok mu? <StyledLink to="/signup">Kayıt Ol</StyledLink>
                    </p>
                    <p align="center" style={{ color: "gray" }}>
                        Şifreni mi unuttun? <StyledLink to="/forgot-password">Şifremi Sıfırla</StyledLink>
                    </p>
                </Card>
            </SignUpContainer>
        </div>
    );
};

export default SigninPage;
